import React, { useState, useEffect, useMemo } from "react";
import { Plus, Car, Trash2, Link2, Unlink, Pencil } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import {
  parkingSlots as api,
  units as unitsApi,
  residents as residentsApi,
  vehicles as vehiclesApi,
  type ParkingSlot,
  type Unit,
  type Resident,
  type Vehicle,
  type ApiError,
  type CsvImportResult,
} from "../../services/api";
import DataTable from "../../components/DataTable";
import SearchSelect from "../../components/SearchSelect";
import BulkImportExport from "../../components/BulkImportExport";
import { useServerTable } from "../../hooks/useServerTable";
import { downloadBlob } from "../../utils/downloadBlob";

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};
const actionBtn = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px 6px",
  borderRadius: 6,
  color: "var(--nx-text-muted)",
  display: "inline-flex",
  alignItems: "center",
};

const CREATE_INIT = { slotNumber: "", type: "CAR" as ParkingSlot["type"] };
const ASSIGN_INIT = { unitId: "", residentId: "", vehicleNumber: "" };

const ParkingPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const isAdmin =
    user?.role === "SOCIETY_ADMIN" || user?.role === "SUPER_ADMIN";
  // Browsing the full unit directory (GET /units list) is admin/staff-only on the
  // backend (see SecurityConfig) — residents/tenants only ever assign/view their
  // own slot, so skip the forbidden call for those roles instead of 401ing.
  const canBrowseUnits = user?.role !== "RESIDENT" && user?.role !== "TENANT";

  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterUnitId, setFilterUnitId] = useState("");
  const [filterResidentId, setFilterResidentId] = useState("");
  const [filterSlotNumber, setFilterSlotNumber] = useState("");
  const [filterVehicleNumber, setFilterVehicleNumber] = useState("");

  const {
    rows,
    totalElements,
    totalPages,
    loading,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortField,
    sortDir,
    setSort,
    sort,
    q,
    setQ,
    refetch,
  } = useServerTable<ParkingSlot>(
    (params) =>
      societyId
        ? api.list(societyId, {
            ...params,
            type: filterType || undefined,
            status: filterStatus || undefined,
            unitId: filterUnitId || undefined,
            residentId: filterResidentId || undefined,
            slotNumber: filterSlotNumber || undefined,
            vehicleNumber: filterVehicleNumber || undefined,
          })
        : Promise.resolve({ content: [], totalElements: 0, totalPages: 0 }),
    [
      societyId,
      filterType,
      filterStatus,
      filterUnitId,
      filterResidentId,
      filterSlotNumber,
      filterVehicleNumber,
    ],
  );

  const [unitList, setUnitList] = useState<Unit[]>([]);
  const [residentList, setResidentList] = useState<Resident[]>([]);
  const [vehicleList, setVehicleList] = useState<Vehicle[]>([]);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(CREATE_INIT);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const [editingSlot, setEditingSlot] = useState<ParkingSlot | null>(null);
  const [editForm, setEditForm] = useState(CREATE_INIT);
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState("");

  const [assigningSlot, setAssigningSlot] = useState<ParkingSlot | null>(null);
  const [assignForm, setAssignForm] = useState(ASSIGN_INIT);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState("");

  const [confirmSlot, setConfirmSlot] = useState<ParkingSlot | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [actionError, setActionError] = useState("");
  const [selectedSlotIds, setSelectedSlotIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulkDeleteOpen, setConfirmBulkDeleteOpen] = useState(false);

  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState<CsvImportResult | null>(
    null,
  );

  useEffect(() => {
    if (!societyId) return;
    if (canBrowseUnits) {
      unitsApi
        .list(societyId, { size: 2000 })
        .then((page) => setUnitList(page.content))
        .catch(() => {});
    }
    residentsApi
      .list(societyId, { size: 2000 })
      .then((page) => setResidentList(page.content))
      .catch(() => {});
    if (isAdmin) {
      vehiclesApi
        .list(societyId)
        .then((list) => setVehicleList(list))
        .catch(() => {});
    }
  }, [societyId, canBrowseUnits, isAdmin]);

  const unitLabel = (id?: string) => {
    const u = unitList.find((u) => u.id === id);
    if (!u) return "—";
    return u.blockName ? `${u.blockName} — ${u.unitNumber}` : u.unitNumber;
  };
  const sortedUnitList = useMemo(
    () =>
      [...unitList].sort((a, b) => {
        const blockCmp = (a.blockName || "").localeCompare(
          b.blockName || "",
          undefined,
          { numeric: true, sensitivity: "base" },
        );
        if (blockCmp !== 0) return blockCmp;
        return a.unitNumber.localeCompare(b.unitNumber, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }),
    [unitList],
  );
  const residentsForUnit = residentList
    .filter((r) => r.unitId === assignForm.unitId && r.active)
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

  const sortedResidentList = useMemo(
    () =>
      [...residentList].sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [residentList],
  );

  // Registered vehicles to suggest for the "Vehicle Number" field — scoped to the
  // selected resident when one is picked, otherwise to everyone on the selected unit.
  const vehiclesForSelection = vehicleList.filter((v) => {
    if (!v.active) return false;
    if (assignForm.residentId) return v.residentId === assignForm.residentId;
    if (assignForm.unitId) return v.unitId === assignForm.unitId;
    return false;
  });

  const openCreate = () => {
    setCreateForm(CREATE_INIT);
    setCreateError("");
    setShowCreate(true);
  };
  const closeCreate = () => setShowCreate(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setCreating(true);
    setCreateError("");
    try {
      await api.create(societyId, createForm);
      refetch();
      closeCreate();
    } catch (err) {
      setCreateError(
        (err as ApiError).message || "Failed to create parking slot.",
      );
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (slot: ParkingSlot) => {
    setEditingSlot(slot);
    setEditForm({ slotNumber: slot.slotNumber, type: slot.type });
    setEditError("");
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !editingSlot) return;
    setEditing(true);
    setEditError("");
    try {
      await api.update(societyId, editingSlot.id, editForm);
      refetch();
      setEditingSlot(null);
    } catch (err) {
      setEditError(
        (err as ApiError).message || "Failed to update parking slot.",
      );
    } finally {
      setEditing(false);
    }
  };

  const openAssign = (slot: ParkingSlot) => {
    setAssigningSlot(slot);
    setAssignForm(ASSIGN_INIT);
    setAssignError("");
  };

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !assigningSlot) return;
    if (!assignForm.unitId) {
      setAssignError("Please select a unit.");
      return;
    }
    setAssigning(true);
    setAssignError("");
    try {
      await api.assign(societyId, assigningSlot.id, {
        unitId: assignForm.unitId,
        residentId: assignForm.residentId || undefined,
        vehicleNumber: assignForm.vehicleNumber || undefined,
      });
      refetch();
      setAssigningSlot(null);
    } catch (err) {
      setAssignError(
        (err as ApiError).message || "Failed to assign parking slot.",
      );
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassign = async (slot: ParkingSlot) => {
    if (!societyId) return;
    setActionError("");
    try {
      await api.unassign(societyId, slot.id);
      refetch();
    } catch {
      setActionError("Failed to unassign parking slot.");
    }
  };

  const handleDelete = (slot: ParkingSlot) => {
    setConfirmSlot(slot);
    setDeleteError("");
  };
  const handleBulkDelete = () => {
    if (selectedSlotIds.length === 0) return;
    setDeleteError("");
    setConfirmBulkDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!societyId || !confirmSlot) return;
    setDeletingId(confirmSlot.id);
    try {
      await api.delete(societyId, confirmSlot.id);
      refetch();
      setConfirmSlot(null);
      setSelectedSlotIds((current) =>
        current.filter((id) => id !== confirmSlot.id),
      );
    } catch {
      setDeleteError("Failed to delete parking slot.");
    } finally {
      setDeletingId(null);
    }
  };

  const confirmBulkDelete = async () => {
    if (!societyId || selectedSlotIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await api.bulkDelete(societyId, selectedSlotIds);
      refetch();
      setSelectedSlotIds([]);
      setConfirmBulkDeleteOpen(false);
    } catch {
      setDeleteError(
        "Failed to delete selected parking slots. Please try again.",
      );
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleImport = async (file: File) => {
    if (!societyId) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await api.importCsv(societyId, file);
      setImportResult(result);
      refetch();
    } catch (err) {
      setImportResult({
        created: 0,
        failed: 1,
        errors: [(err as ApiError).message || "Import failed"],
      });
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    if (!societyId) return;
    setExporting(true);
    try {
      const blob = await api.exportCsv(societyId, sort);
      downloadBlob(blob, "parking-slots-export.csv");
    } catch {
      /* ignore */
    } finally {
      setExporting(false);
    }
  };

  return (
    <PageLayout>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: 22,
              fontWeight: 700,
              color: "var(--nx-text-primary)",
              letterSpacing: "-0.03em",
              marginBottom: 4,
            }}
          >
            Parking
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            {totalElements} slots ·{" "}
            {rows.filter((s) => s.status === "ASSIGNED").length} assigned on
            this page
          </p>
        </div>
        {isAdmin && (
          <div style={{ display: "flex", gap: 8 }}>
            <BulkImportExport
              onImport={handleImport}
              onExport={handleExport}
              importing={importing}
              exporting={exporting}
            />
            <button
              onClick={openCreate}
              className="nx-btn nx-btn-primary"
              style={{ gap: 6 }}
            >
              <Plus size={14} /> Add Slot
            </button>
          </div>
        )}
      </div>

      {importResult && (
        <div
          style={{
            padding: "10px 16px",
            background: "rgba(34,197,94,0.08)",
            border: "1px solid rgba(34,197,94,0.25)",
            borderRadius: 10,
            fontSize: 13,
            color: "#16A34A",
            marginBottom: 16,
          }}
        >
          Imported {importResult.created} parking slots
          {importResult.failed > 0
            ? `, ${importResult.failed} failed: ${importResult.errors.join("; ")}`
            : "."}
        </div>
      )}

      {actionError && (
        <div
          style={{
            padding: "10px 14px",
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.25)",
            borderRadius: 10,
            fontSize: 12,
            color: "#EF4444",
            marginBottom: 16,
          }}
        >
          {actionError}
        </div>
      )}

      <div
        className="nx-card"
        style={{
          padding: 16,
          marginBottom: 16,
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "flex-end",
        }}
      >
        <div style={{ minWidth: 140 }}>
          <label style={lbl}>Slot</label>
          <input
            value={filterSlotNumber}
            onChange={(e) => setFilterSlotNumber(e.target.value)}
            placeholder="B-12"
            style={inp}
          />
        </div>
        <div style={{ minWidth: 120 }}>
          <label style={lbl}>Type</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={inp}
          >
            <option value="">All</option>
            <option value="CAR">Car</option>
            <option value="BIKE">Bike</option>
            <option value="VISITOR">Visitor</option>
          </select>
        </div>
        <div style={{ minWidth: 120 }}>
          <label style={lbl}>Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={inp}
          >
            <option value="">All</option>
            <option value="VACANT">Vacant</option>
            <option value="ASSIGNED">Assigned</option>
          </select>
        </div>
        <div style={{ minWidth: 180 }}>
          <label style={lbl}>Unit</label>
          <SearchSelect
            value={filterUnitId}
            onChange={setFilterUnitId}
            options={sortedUnitList.map((u) => ({
              value: u.id,
              label: u.blockName
                ? `${u.blockName} — ${u.unitNumber}`
                : u.unitNumber,
            }))}
            placeholder="All units"
          />
        </div>
        <div style={{ minWidth: 180 }}>
          <label style={lbl}>Resident / Tenant</label>
          <SearchSelect
            value={filterResidentId}
            onChange={setFilterResidentId}
            options={sortedResidentList.map((r) => ({
              value: r.id,
              label:
                r.type === "tenant" ? `${r.fullName} (Tenant)` : r.fullName,
            }))}
            placeholder="All residents"
          />
        </div>
        <div style={{ minWidth: 160 }}>
          <label style={lbl}>Vehicle</label>
          <input
            value={filterVehicleNumber}
            onChange={(e) => setFilterVehicleNumber(e.target.value)}
            placeholder="TN 01 AB 1234"
            style={inp}
          />
        </div>
        {(filterType ||
          filterStatus ||
          filterUnitId ||
          filterResidentId ||
          filterSlotNumber ||
          filterVehicleNumber) && (
          <button
            type="button"
            className="nx-btn nx-btn-outline"
            onClick={() => {
              setFilterType("");
              setFilterStatus("");
              setFilterUnitId("");
              setFilterResidentId("");
              setFilterSlotNumber("");
              setFilterVehicleNumber("");
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            {
              key: "slotNumber",
              header: "Slot",
              cellStyle: { fontWeight: 500 },
              sortable: true,
              sortKey: "slotNumber",
              render: (s: ParkingSlot) => s.slotNumber,
            },
            {
              key: "type",
              header: "Type",
              sortable: true,
              sortKey: "type",
              render: (s: ParkingSlot) => s.type,
            },
            {
              key: "status",
              header: "Status",
              sortable: true,
              sortKey: "status",
              render: (s: ParkingSlot) => (
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: 99,
                    fontSize: 10,
                    fontWeight: 500,
                    background:
                      s.status === "ASSIGNED"
                        ? "rgba(16,185,129,0.1)"
                        : "rgba(148,163,184,0.1)",
                    color: s.status === "ASSIGNED" ? "#10B981" : "#64748B",
                  }}
                >
                  {s.status}
                </span>
              ),
            },
            {
              key: "unit",
              header: "Unit",
              cellStyle: { color: "var(--nx-text-secondary)" },
              sortable: true,
              sortKey: "un.unitNumber",
              render: (s: ParkingSlot) => unitLabel(s.assignedUnitId),
            },
            {
              key: "resident",
              header: "Resident",
              cellStyle: { color: "var(--nx-text-secondary)" },
              sortable: true,
              sortKey: "res.fullName",
              render: (s: ParkingSlot) => {
                const resident = residentList.find(
                  (r) => r.id === s.assignedResidentId,
                );
                return resident
                  ? resident.fullName
                  : s.assignedResidentId
                    ? "Resident"
                    : "—";
              },
            },
            {
              key: "vehicle",
              header: "Vehicle",
              cellStyle: { color: "var(--nx-text-muted)" },
              sortable: true,
              sortKey: "vehicleNumber",
              render: (s: ParkingSlot) => s.vehicleNumber || "—",
            },
            {
              key: "actions",
              header: "",
              cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
              render: (s: ParkingSlot) =>
                isAdmin ? (
                  <>
                    <button
                      onClick={() => openEdit(s)}
                      style={actionBtn}
                      title="Edit slot"
                    >
                      <Pencil size={14} />
                    </button>
                    {s.status === "VACANT" ? (
                      <button
                        onClick={() => openAssign(s)}
                        style={actionBtn}
                        title="Assign slot"
                      >
                        <Link2 size={14} />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUnassign(s)}
                        style={actionBtn}
                        title="Unassign slot"
                      >
                        <Unlink size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(s)}
                      disabled={deletingId === s.id}
                      style={{
                        ...actionBtn,
                        color: "#EF4444",
                        opacity: deletingId === s.id ? 0.4 : 1,
                      }}
                      title="Delete slot"
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                ) : null,
            },
          ]}
          rows={rows}
          rowKey={(s) => s.id}
          loading={loading}
          emptyIcon={
            <Car size={32} style={{ color: "var(--nx-text-muted)" }} />
          }
          emptyText="No parking slots yet."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          sortField={sortField}
          sortDir={sortDir}
          onSortChange={setSort}
          searchValue={q}
          onSearchChange={setQ}
          searchPlaceholder="Search slots…"
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          selectedRowIds={selectedSlotIds}
          onSelectionChange={setSelectedSlotIds}
          toolbarActions={
            isAdmin ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                {selectedSlotIds.length > 0 && (
                  <span
                    style={{ fontSize: 12, color: "var(--nx-text-secondary)" }}
                  >
                    {selectedSlotIds.length} selected
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  disabled={selectedSlotIds.length === 0}
                  className="nx-btn nx-btn-outline"
                  style={{ gap: 6 }}
                >
                  <Trash2 size={14} /> Delete selected
                </button>
              </div>
            ) : undefined
          }
        />
      </div>

      <ConfirmDialog
        open={confirmSlot !== null}
        variant="danger"
        title={`Delete slot "${confirmSlot?.slotNumber}"?`}
        message="This parking slot will be permanently removed."
        confirmLabel="Delete Slot"
        isLoading={deletingId !== null}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => {
          setConfirmSlot(null);
          setDeleteError("");
        }}
      />
      <ConfirmDialog
        open={confirmBulkDeleteOpen}
        variant="danger"
        title="Delete selected parking slots?"
        message={`This will permanently remove ${selectedSlotIds.length} selected parking slot${selectedSlotIds.length !== 1 ? "s" : ""}.`}
        confirmLabel="Delete Slots"
        isLoading={bulkDeleting}
        error={deleteError}
        onConfirm={confirmBulkDelete}
        onCancel={() => {
          setConfirmBulkDeleteOpen(false);
          setDeleteError("");
        }}
      />

      {showCreate && (
        <Modal
          title="Add Parking Slot"
          submitLabel="Add Slot"
          error={createError}
          isLoading={creating}
          onClose={closeCreate}
          onSubmit={handleCreate}
        >
          <div>
            <label style={lbl}>Slot Number *</label>
            <input
              value={createForm.slotNumber}
              onChange={(e) =>
                setCreateForm((p) => ({ ...p, slotNumber: e.target.value }))
              }
              required
              placeholder="B-12"
              style={inp}
              autoFocus
            />
          </div>
          <div>
            <label style={lbl}>Type *</label>
            <select
              value={createForm.type}
              onChange={(e) =>
                setCreateForm((p) => ({
                  ...p,
                  type: e.target.value as ParkingSlot["type"],
                }))
              }
              style={inp}
            >
              <option value="CAR">Car</option>
              <option value="BIKE">Bike</option>
              <option value="VISITOR">Visitor</option>
            </select>
          </div>
        </Modal>
      )}

      {editingSlot && (
        <Modal
          title={`Edit Slot ${editingSlot.slotNumber}`}
          submitLabel="Save Changes"
          error={editError}
          isLoading={editing}
          onClose={() => setEditingSlot(null)}
          onSubmit={handleEdit}
        >
          <div>
            <label style={lbl}>Slot Number *</label>
            <input
              value={editForm.slotNumber}
              onChange={(e) =>
                setEditForm((p) => ({ ...p, slotNumber: e.target.value }))
              }
              required
              placeholder="B-12"
              style={inp}
              autoFocus
            />
          </div>
          <div>
            <label style={lbl}>Type *</label>
            <select
              value={editForm.type}
              onChange={(e) =>
                setEditForm((p) => ({
                  ...p,
                  type: e.target.value as ParkingSlot["type"],
                }))
              }
              style={inp}
            >
              <option value="CAR">Car</option>
              <option value="BIKE">Bike</option>
              <option value="VISITOR">Visitor</option>
            </select>
          </div>
        </Modal>
      )}

      {assigningSlot && (
        <Modal
          title={`Assign Slot ${assigningSlot.slotNumber}`}
          submitLabel="Assign"
          error={assignError}
          isLoading={assigning}
          onClose={() => setAssigningSlot(null)}
          onSubmit={handleAssign}
        >
          <div>
            <label style={lbl}>Unit *</label>
            <SearchSelect
              value={assignForm.unitId}
              onChange={(v) => {
                const residentsOnUnit = residentList.filter(
                  (r) => r.unitId === v && r.active,
                );
                const residentId =
                  residentsOnUnit.length === 1 ? residentsOnUnit[0].id : "";
                const residentVehicles = vehicleList.filter(
                  (veh) => veh.active && veh.residentId === residentId,
                );
                setAssignForm((p) => ({
                  ...p,
                  unitId: v,
                  residentId,
                  vehicleNumber:
                    residentVehicles.length === 1
                      ? residentVehicles[0].registrationNumber
                      : "",
                }));
              }}
              options={sortedUnitList.map((u) => ({
                value: u.id,
                label: u.blockName ? `${u.blockName} — ${u.unitNumber}` : u.unitNumber,
              }))}
              placeholder="Search units…"
              required
            />
          </div>
          <div>
            <label style={lbl}>Resident / Tenant</label>
            <SearchSelect
              value={assignForm.residentId}
              onChange={(v) => {
                const residentVehicles = vehicleList.filter(
                  (veh) => veh.active && veh.residentId === v,
                );
                setAssignForm((p) => ({
                  ...p,
                  residentId: v,
                  vehicleNumber:
                    residentVehicles.length === 1
                      ? residentVehicles[0].registrationNumber
                      : p.vehicleNumber,
                }));
              }}
              options={residentsForUnit.map((r) => ({
                value: r.id,
                label:
                  r.type === "tenant"
                    ? `${r.fullName} (Tenant)`
                    : r.fullName,
              }))}
              placeholder={
                !assignForm.unitId
                  ? "Select a unit first"
                  : residentsForUnit.length === 0
                    ? "No resident/tenant on this unit"
                    : "Select a resident/tenant (optional)"
              }
              disabled={!assignForm.unitId || residentsForUnit.length === 0}
            />
          </div>
          <div>
            <label style={lbl}>Vehicle Number</label>
            <input
              value={assignForm.vehicleNumber}
              onChange={(e) =>
                setAssignForm((p) => ({ ...p, vehicleNumber: e.target.value }))
              }
              placeholder="TN 01 AB 1234"
              style={inp}
            />
            {vehiclesForSelection.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6,
                  marginTop: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--nx-text-muted)",
                    alignSelf: "center",
                  }}
                >
                  Registered:
                </span>
                {vehiclesForSelection.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() =>
                      setAssignForm((p) => ({
                        ...p,
                        vehicleNumber: v.registrationNumber,
                      }))
                    }
                    style={{
                      padding: "3px 10px",
                      borderRadius: 99,
                      fontSize: 11,
                      fontWeight: 500,
                      border:
                        assignForm.vehicleNumber === v.registrationNumber
                          ? "1px solid var(--nx-blue)"
                          : "1px solid var(--nx-border)",
                      background:
                        assignForm.vehicleNumber === v.registrationNumber
                          ? "rgba(59,130,246,0.1)"
                          : "var(--nx-surface-3)",
                      color:
                        assignForm.vehicleNumber === v.registrationNumber
                          ? "var(--nx-blue)"
                          : "var(--nx-text-secondary)",
                      cursor: "pointer",
                    }}
                  >
                    {v.registrationNumber} ·{" "}
                    {v.vehicleType === "CAR"
                      ? "Car"
                      : v.vehicleType === "BIKE"
                        ? "Bike"
                        : "Other"}
                  </button>
                ))}
              </div>
            ) : (
              (assignForm.residentId || assignForm.unitId) && (
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--nx-text-muted)",
                    marginTop: 6,
                  }}
                >
                  No registered vehicle found — enter a new plate number
                  above.
                </p>
              )
            )}
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default ParkingPage;
